import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Activity, Clock3, Users } from "lucide-react";
import V2AvailabilityCard, { V2AvailabilitySkeleton } from "../overview/V2AvailabilityCard";
import V2BusyFrame, { V2RefreshChip } from "../overview/V2BusyFrame";
import V2BlockOrderModal from "../overview/V2BlockOrderModal";
import V2ExternalLoadPanel from "../overview/V2ExternalLoadPanel";
import V2FilterDropdown from "../overview/V2FilterDropdown";
import V2InternalLoadPanel from "../overview/V2InternalLoadPanel";
import V2SessionDayPicker from "../overview/V2SessionDayPicker";
import useInternalBlockOrder from "../overview/useInternalBlockOrder";
import useTeamOverviewInternalLoad from "../overview/useTeamOverviewInternalLoad";
import { matchesPosition, V2_POSITIONS } from "../overview/v2OverviewUtils";

const OUTLINE_BTN =
  "inline-flex min-h-[34px] items-center gap-1.5 rounded-[5px] border border-[rgba(98,94,166,0.62)] bg-[rgba(17,12,61,0.52)] px-3 text-[12px] text-[#e5e2ff] hover:border-v2-cyan hover:bg-v2-accent";

const TAB =
  "relative flex items-center gap-1.5 border-0 bg-transparent py-3.5 pb-3 text-[13px] text-v2-subtle";

const SEGMENT =
  "min-h-8 border-0 border-r border-[rgba(98,94,166,0.35)] bg-transparent px-3.5 text-[12px] text-v2-subtle last:border-r-0";

const MENU_ITEM =
  "flex min-h-8 w-full items-center gap-2 rounded-[5px] border-0 bg-transparent px-2 text-left text-[12px] text-[#f1efff] hover:bg-[rgba(98,94,166,0.2)]";

function sessionLabel(session, t) {
  if (!session) return t("v2.overview.session_prefix");
  const period = t(`v2.overview.sessions.${session.period}`);
  const kind = t(`v2.overview.session_kind.${session.kind}`);
  const time = session.start_time ? ` · ${session.start_time}` : "";
  return `${t("v2.overview.session_prefix")} ${period} · ${kind}${time}`;
}

export default function V2TeamOverviewPage({ clubId } = {}) {
  const { t } = useTranslation();
  const [mainTab, setMainTab] = useState("loads");
  const [loadKind, setLoadKind] = useState("internal");
  const [positions, setPositions] = useState([]);
  const [blockOrderOpen, setBlockOrderOpen] = useState(false);
  const overview = useTeamOverviewInternalLoad(clubId);
  const blockOrder = useInternalBlockOrder();

  const filteredPlayers = useMemo(
    () => overview.players.filter((player) => matchesPosition(player, positions)),
    [overview.players, positions]
  );

  const positionLabel = positions.length === 0
    ? t("v2.common.all_positions")
    : positions.length === 1
      ? t(`v2.overview.positions.${positions[0].toLowerCase()}`)
      : t("v2.overview.positions_selected", { count: positions.length });

  const togglePosition = (id) => {
    setPositions((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  };

  const selectedSession = overview.sessions.find((item) => item.id === overview.sessionId)
    || overview.session;
  const dataBusy = overview.sessionsLoading || overview.snapshotLoading || overview.trendsLoading;
  const initialLoading = !overview.availability && (overview.sessionsLoading || overview.snapshotLoading);
  const snapshotRefreshing = Boolean(overview.availability) && overview.snapshotLoading;

  return (
    <div className="mx-auto max-w-[1600px] px-[38px] pb-[52px] pt-7 max-[900px]:px-5 max-[900px]:pb-10 max-[900px]:pt-6">
      {dataBusy ? (
        <div className="v2-fetching-dock" role="status" aria-live="polite">
          <div className="v2-fetching-dock-bar" aria-hidden="true" />
          <div className="v2-indeterminate" aria-hidden="true" />
          <V2RefreshChip label={t("v2.overview.fetching")} />
        </div>
      ) : null}
      <header className="flex items-start justify-between gap-6 border-b border-[rgba(98,94,166,0.22)] pb-[19px] max-[650px]:flex-col max-[650px]:items-start">
        <div>
          <p className="mb-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-v2-cyan">
            {t("v2.overview.eyebrow")}
          </p>
          <h1 className="m-0 font-display text-[36px] font-bold uppercase leading-none text-v2-foreground">
            {t("v2.overview.title")}
          </h1>
          <p className="mt-2 text-[13px] text-v2-muted">{t("v2.overview.subtitle")}</p>
        </div>
        <div className="flex items-center gap-[7px] max-[650px]:flex-wrap">
          <button
            type="button"
            className={OUTLINE_BTN}
            onClick={() => setBlockOrderOpen(true)}
          >
            <span aria-hidden="true">↕</span>
            {t("v2.overview.block_order")}
          </button>
          <button type="button" className={OUTLINE_BTN}>
            <span aria-hidden="true">⇩</span>
            {t("v2.common.export")}
          </button>
        </div>
      </header>

      <div className="flex gap-[25px] border-b border-[rgba(98,94,166,0.22)]" role="tablist" aria-label={t("v2.overview.tabs_label")}>
        <button
          type="button"
          className={`${TAB} ${mainTab === "loads" ? "text-v2-foreground after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-v2-cyan after:content-['']" : ""}`}
          role="tab"
          aria-selected={mainTab === "loads"}
          onClick={() => setMainTab("loads")}
        >
          <Activity size={16} />
          {t("v2.overview.loads")}
        </button>
        <button
          type="button"
          className={`${TAB} ${mainTab === "feedback" ? "text-v2-foreground after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-v2-cyan after:content-['']" : ""}`}
          role="tab"
          aria-selected={mainTab === "feedback"}
          onClick={() => setMainTab("feedback")}
        >
          {t("v2.overview.player_feedback")}
        </button>
      </div>

      {mainTab === "feedback" ? (
        <section className="mt-6 rounded-[10px] border border-dashed border-[rgba(98,94,166,0.45)] p-7 text-v2-muted">
          <h2 className="mb-2 font-display uppercase text-[#f4f2ff]">{t("v2.overview.player_feedback")}</h2>
          <p>{t("v2.overview.feedback_placeholder")}</p>
        </section>
      ) : (
        <>
          <div className="flex items-center justify-between gap-[15px] py-3 pb-[18px] max-[650px]:flex-col max-[650px]:items-start">
            <div className="flex overflow-hidden rounded-[5px] border border-[rgba(98,94,166,0.55)]" role="group" aria-label={t("v2.overview.load_type")}>
              <button
                type="button"
                className={`${SEGMENT} ${loadKind === "internal" ? "bg-[rgba(35,30,98,0.95)] text-v2-foreground" : ""}`}
                onClick={() => setLoadKind("internal")}
              >
                {t("v2.overview.internal_load")}
              </button>
              <button
                type="button"
                className={`${SEGMENT} ${loadKind === "external" ? "bg-[rgba(35,30,98,0.95)] text-v2-foreground" : ""}`}
                onClick={() => setLoadKind("external")}
              >
                {t("v2.overview.external_load")}
              </button>
            </div>

            <div className="flex items-center gap-[7px] max-[650px]:flex-wrap">
              <V2FilterDropdown label={positionLabel} icon={Users}>
                {(close) => (
                  <>
                    <button type="button" className={MENU_ITEM} onClick={() => { setPositions([]); close(); }}>
                      {t("v2.common.all_positions")}
                    </button>
                    {V2_POSITIONS.map((position) => (
                      <label key={position.id} className={`${MENU_ITEM} cursor-pointer`}>
                        <input
                          type="checkbox"
                          checked={positions.includes(position.id)}
                          onChange={() => togglePosition(position.id)}
                        />
                        {t(position.labelKey)}
                      </label>
                    ))}
                  </>
                )}
              </V2FilterDropdown>

              <V2SessionDayPicker
                selectedDate={overview.date}
                sessionDates={overview.sessionDates}
                latestDate={overview.latestDate}
                onSelectDate={overview.selectDate}
                loading={overview.sessionsLoading || overview.snapshotLoading}
              />

              {loadKind === "external" ? (
                <V2FilterDropdown
                  label={sessionLabel(selectedSession, t)}
                  icon={Clock3}
                >
                  {overview.daySessions.length === 0 ? (
                    <span className="px-2 py-2 text-[12px] text-[#8f8abf]">{t("v2.overview.no_sessions")}</span>
                  ) : overview.daySessions.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`${MENU_ITEM} ${overview.sessionId === item.id ? "bg-[rgba(98,94,166,0.2)]" : ""}`}
                      onClick={() => overview.setSessionId(item.id)}
                    >
                      {t(`v2.overview.sessions.${item.period}`)}
                      {` · ${t(`v2.overview.session_kind.${item.kind}`)}`}
                      {item.start_time ? ` · ${item.start_time}` : ""}
                    </button>
                  ))}
                </V2FilterDropdown>
              ) : null}
            </div>
          </div>

          {overview.error ? (
            <div className="mb-4 rounded-[10px] border border-[rgba(255,77,94,0.4)] bg-[rgba(255,77,94,0.08)] px-4 py-3 text-[13px] text-[#ff8a96]">
              <p className="m-0">{t("v2.overview.load_error")}</p>
              <button type="button" className="mt-2 border-0 bg-transparent p-0 font-semibold text-[#34cbff]" onClick={overview.reload}>
                {t("v2.common.retry")}
              </button>
            </div>
          ) : null}

          {initialLoading ? (
            <V2BusyFrame loading label={t("v2.overview.fetching")} className="rounded-[10px]">
              <V2AvailabilitySkeleton />
            </V2BusyFrame>
          ) : overview.availability ? (
            <V2AvailabilityCard data={overview.availability} loading={snapshotRefreshing} />
          ) : (
            <p className="py-6 text-[13px] text-v2-muted">{t("v2.overview.load_empty")}</p>
          )}

          {loadKind === "internal" ? (
            <V2InternalLoadPanel
              players={filteredPlayers}
              selectedPositions={positions}
              trends={overview.trends}
              trendPeriod={overview.trendPeriod}
              onTrendPeriodChange={overview.setTrendPeriod}
              clubId={overview.clubId}
              date={overview.date}
              period={overview.period}
              loading={overview.snapshotLoading || initialLoading}
              trendsLoading={overview.trendsLoading}
              blockOrder={blockOrder.order}
            />
          ) : (
            <V2ExternalLoadPanel />
          )}
        </>
      )}

      <V2BlockOrderModal
        open={blockOrderOpen}
        order={blockOrder.order}
        defaultOrder={blockOrder.defaultOrder}
        onSave={blockOrder.save}
        onClose={() => setBlockOrderOpen(false)}
      />
    </div>
  );
}
